import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { DOC_TYPE_CATALOG } from '@dcc-erp/shared';
import type { ApprovalTier, RoleWithPermissions } from '@dcc-erp/shared';

import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useApprovalMatrix,
  useApprovalPreview,
  useReplaceApprovalTiers,
  useRoles,
} from '../lib/queries';

export const Route = createFileRoute('/settings/approval-matrix')({
  component: ApprovalMatrixPage,
});

interface TierRow {
  maxAmount: string; // '' = top tier
  branchId: string; // '' = company-wide (null)
  departmentId: string; // '' = company-wide (null)
  // The maker-checker-approver chain is held split for editing and persisted as
  // a single ordered roleCodes array: [...reviewers, approver]. The approver is
  // always the LAST step; reviewers are the optional leading steps.
  reviewers: string[];
  approver: string; // '' = not yet chosen (a tier requires an approver to be valid)
}

function toRows(tiers: ApprovalTier[]): TierRow[] {
  return tiers
    .slice()
    .sort((a, b) =>
      a.maxAmount == null
        ? 1
        : b.maxAmount == null
          ? -1
          : Number(a.maxAmount) - Number(b.maxAmount),
    )
    .map((t) => {
      // roleCodes is stored reviewers-first, approver-last.
      const codes = t.roleCodes;
      const approver = codes.length > 0 ? codes[codes.length - 1]! : '';
      const reviewers = codes.length > 1 ? codes.slice(0, -1) : [];
      return {
        maxAmount: t.maxAmount == null ? '' : String(t.maxAmount),
        branchId: t.branchId ?? '',
        departmentId: t.departmentId ?? '',
        reviewers,
        approver,
      };
    });
}

// roleCodes order for persistence / preview: reviewers first, approver last.
function chainCodes(r: TierRow): string[] {
  return r.approver ? [...r.reviewers, r.approver] : [...r.reviewers];
}

// TierEditor is remounted via key={docType} whenever docType changes, so the
// initial state from `tiers` is fresh without needing setState-in-effect.
interface TierEditorProps {
  companyId: string | undefined;
  docType: string;
  initialTiers: ApprovalTier[];
  isPending: boolean;
}

function TierEditor({ companyId, docType, initialTiers, isPending }: TierEditorProps) {
  const { data: roles = [] } = useRoles();
  const replace = useReplaceApprovalTiers(companyId);
  const [rows, setRows] = useState<TierRow[]>(() => toRows(initialTiers));
  const [previewAmt, setPreviewAmt] = useState('');
  // Optional branch/cost-center the preview resolves against — lets an admin
  // see the chain that a condition-scoped tier would actually pick. Empty =
  // company-wide (amount-only) resolution.
  const [previewBranch, setPreviewBranch] = useState('');
  const [previewDept, setPreviewDept] = useState('');
  const { data: preview = [] } = useApprovalPreview(companyId, docType, previewAmt, {
    branchId: previewBranch || undefined,
    departmentId: previewDept || undefined,
  });

  // Roles eligible to sit in a chain step (the superuser '*' role is excluded).
  const stepRoles = roles.filter((ro) => !ro.permissions.includes('*'));

  function patch(i: number, p: Partial<TierRow>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  }
  function addReviewer(i: number, code: string) {
    if (!code) return;
    patch(i, { reviewers: [...rows[i]!.reviewers, code] });
  }
  function removeReviewer(i: number, idx: number) {
    patch(i, { reviewers: rows[i]!.reviewers.filter((_, j) => j !== idx) });
  }

  const invalidTier = rows.find((r) => !r.approver);

  async function save() {
    await replace.mutateAsync({
      docType,
      tiers: rows.map((r) => ({
        maxAmount: r.maxAmount === '' ? null : Number(r.maxAmount),
        branchId: r.branchId === '' ? null : r.branchId,
        departmentId: r.departmentId === '' ? null : r.departmentId,
        // approver-last ordered chain; the builder guarantees an approver exists
        // (save is blocked otherwise), so this is never empty here.
        roleCodes: chainCodes(r),
      })),
    });
  }

  return (
    <>
      <div className="filter-row" style={{ gap: 8 }}>
        <button
          className="btn"
          onClick={() =>
            setRows((r) => [
              ...r,
              { maxAmount: '', branchId: '', departmentId: '', reviewers: [], approver: '' },
            ])
          }
        >
          + เพิ่มชั้น
        </button>
        <button
          className="btn primary"
          style={{ marginLeft: 'auto' }}
          disabled={replace.isPending || !!invalidTier}
          title={invalidTier ? 'ทุกชั้นต้องมีผู้อนุมัติ (ผู้อนุมัติคนสุดท้าย)' : undefined}
          onClick={() => void save()}
        >
          บันทึก
        </button>
      </div>
      {invalidTier && (
        <div className="meta" style={{ color: 'var(--danger, #c0392b)' }}>
          ทุกชั้นต้องระบุผู้อนุมัติ (ผู้อนุมัติขั้นสุดท้าย) อย่างน้อย 1 คน
        </div>
      )}
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>วงเงินสูงสุด (ว่าง = ชั้นบนสุด)</th>
              <th style={{ width: 200 }}>เงื่อนไข สาขา / หน่วยงาน (ว่าง = ทุกหน่วยงาน)</th>
              <th>สายอนุมัติ: ผู้ตรวจสอบ → ผู้อนุมัติ</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input
                      className="input tnum"
                      type="number"
                      min={0}
                      value={r.maxAmount}
                      onChange={(e) => patch(i, { maxAmount: e.target.value })}
                      placeholder="ไม่จำกัด"
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <BranchSelect
                        companyId={companyId}
                        value={r.branchId}
                        onChange={(id) => patch(i, { branchId: id, departmentId: '' })}
                      />
                      <DepartmentSelect
                        companyId={companyId}
                        value={r.departmentId}
                        onChange={(id) => patch(i, { departmentId: id })}
                        branchId={r.branchId}
                      />
                    </div>
                  </td>
                  <td>
                    <ChainBuilder
                      row={r}
                      stepRoles={stepRoles}
                      onAddReviewer={(code) => addReviewer(i, code)}
                      onRemoveReviewer={(idx) => removeReviewer(i, idx)}
                      onSetApprover={(code) => patch(i, { approver: code })}
                    />
                  </td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>ทดสอบ (preview)</h3>
        </div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>จำนวนเงิน</span>
            <input
              className="input tnum"
              type="number"
              value={previewAmt}
              onChange={(e) => setPreviewAmt(e.target.value)}
              placeholder="เช่น 300000"
            />
            {/* Optional scope: preview the chain a condition-scoped tier resolves
                to. Leave blank to preview the company-wide (amount-only) chain. */}
            <span className="meta">เงื่อนไข (ไม่บังคับ)</span>
            <BranchSelect
              companyId={companyId}
              value={previewBranch}
              onChange={(id) => {
                setPreviewBranch(id);
                setPreviewDept('');
              }}
            />
            <DepartmentSelect
              companyId={companyId}
              value={previewDept}
              onChange={(id) => setPreviewDept(id)}
              branchId={previewBranch}
            />
          </div>
          <span className="meta">
            {preview.length ? preview.map((s) => `${s.stepNo}. ${s.roleLabel}`).join('  →  ') : '—'}
          </span>
        </div>
      </div>
    </>
  );
}

// The maker-checker-approver chain builder for one tier. Reviewers (ผู้ตรวจสอบ)
// are an ordered add/remove list; the approver (ผู้อนุมัติ) is a single required
// final step. The maker (ผู้จัดทำ) is the submitter — step 0, never configured.
interface ChainBuilderProps {
  row: TierRow;
  stepRoles: RoleWithPermissions[];
  onAddReviewer: (code: string) => void;
  onRemoveReviewer: (idx: number) => void;
  onSetApprover: (code: string) => void;
}

function ChainBuilder({
  row,
  stepRoles,
  onAddReviewer,
  onRemoveReviewer,
  onSetApprover,
}: ChainBuilderProps) {
  const [adding, setAdding] = useState('');
  const labelOf = (code: string) => stepRoles.find((ro) => ro.code === code)?.nameTh ?? code;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Maker — informational; not a chain row */}
      <div className="meta">ผู้จัดทำ (maker): ผู้ส่งอนุมัติเอกสาร — ไม่นับเป็นขั้นในสายอนุมัติ</div>

      {/* Reviewers — ordered add/remove list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>ผู้ตรวจสอบ (reviewers)</span>
        {row.reviewers.length === 0 && <span className="muted">— ไม่มีผู้ตรวจสอบ —</span>}
        {row.reviewers.map((code, idx) => (
          <div key={`${code}-${idx}`} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="meta" style={{ width: 22 }}>
              {idx + 1}.
            </span>
            <span className="code">{code}</span>
            <span style={{ fontSize: 12 }}>{labelOf(code)}</span>
            <button
              className="btn sm ghost"
              style={{ marginLeft: 'auto' }}
              onClick={() => onRemoveReviewer(idx)}
            >
              ✕
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            className="select"
            value={adding}
            onChange={(e) => {
              onAddReviewer(e.target.value);
              setAdding('');
            }}
          >
            <option value="">+ เพิ่มผู้ตรวจสอบ…</option>
            {stepRoles.map((ro) => (
              <option key={ro.id} value={ro.code}>
                {ro.code} — {ro.nameTh}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Approver — single required final step */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>ผู้อนุมัติ (approver) *</span>
        <select
          className="select"
          value={row.approver}
          onChange={(e) => onSetApprover(e.target.value)}
          style={!row.approver ? { borderColor: 'var(--danger, #c0392b)' } : undefined}
        >
          <option value="">— เลือกผู้อนุมัติขั้นสุดท้าย * —</option>
          {stepRoles.map((ro) => (
            <option key={ro.id} value={ro.code}>
              {ro.code} — {ro.nameTh}
            </option>
          ))}
        </select>
      </div>

      {/* Resolved order preview */}
      <div className="meta">ลำดับ: {chainCodes(row).join(' → ') || '—'}</div>
    </div>
  );
}

function ApprovalMatrixPage() {
  const companyId = useActiveCompanyId();
  const [docType, setDocType] = useState<string>(DOC_TYPE_CATALOG[0].code);
  const { data: tiers = [], isPending } = useApprovalMatrix(companyId, docType);

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'เกณฑ์อนุมัติ']}
        title="เกณฑ์อนุมัติตามวงเงิน (Approval Matrix)"
        actions={<HelpButton guide="settings-approval-matrix" />}
      />
      <div className="filter-row" style={{ gap: 8 }}>
        <label>ประเภทเอกสาร</label>
        <select className="select" value={docType} onChange={(e) => setDocType(e.target.value)}>
          {DOC_TYPE_CATALOG.map((d) => (
            <option key={d.code} value={d.code}>
              {d.code} — {d.label}
            </option>
          ))}
        </select>
      </div>
      {/* key={docType} remounts TierEditor on docType change, so initial state is always fresh */}
      <TierEditor
        key={docType}
        companyId={companyId}
        docType={docType}
        initialTiers={tiers}
        isPending={isPending}
      />
    </div>
  );
}
